import Bugsnag from '@bugsnag/js';
import React from 'react';

import Button from '@/core/components/Button';
import Icons from '@/core/components/Icons';
import { useAdsContext } from '@/core/lib/ads/ads.context';
import type { ContactAction } from '@/core/lib/new-architecture/domain/donation.domain';
import Store from '@/core/lib/new-architecture/store';
import { useRouterContext } from '@/core/lib/router/router.context';
import Routes from '@/core/lib/router/routes';
import { useTranslationContext } from '@/core/lib/translation/translation.context';

interface ContactActionComponentProps {
  action: ContactAction;
  variant?: 'ghost' | 'primary';
}

const ContactActionComponent: React.FC<ContactActionComponentProps> = ({ action, variant = 'primary' }) => {
  const { t } = useTranslationContext(['common']);
  const { data: current } = Store.current.useCurrent();
  const { currentPage, push } = useRouterContext();
  const { showReward, showRewardText } = useAdsContext();

  const href = action.conversationId ? new Routes.ConversationRoute(action.conversationId) : new Routes.ConversationFromDonationRoute(action.donationId);

  const onContactDonor = async () => {
    if (current) {
      try {
        await showReward();
      } catch (err) {
        Bugsnag.notify(err as Error);
      } finally {
        push(href);
      }
    } else {
      currentPage.updateCommon(prev => ({ ...prev, openAuth: true, redirectTo: href.resolve() }));
      push(currentPage);
    }
  };

  const contactNotAllowed = !!current && ((!action.canCreate && action.canCreateReason !== 'conv_exist') || action.isBlocked);

  return (
    <div>
      <Button variant={`${contactNotAllowed ? 'ghost' : variant}`} isDisabled={contactNotAllowed} onClick={onContactDonor} iconLeft="message-square">
        {t(action.text, { ns: 'common' })}
      </Button>
      {showRewardText && (
        <section className="text-body-secondary mt-6 flex items-center gap-2 rounded-2 bg-bg-pale p-3 font-medium text-content-placeholder">
          <Icons icon="play-circle" size="24" />
          <div>
            <p>{t('components.donation.details.reward.text', { ns: 'common' })}</p>
            <a className="underline underline-offset-2" href={new Routes.ExternalRoute('faqAds').resolve()}>
              {t('components.donation.details.reward.link', { ns: 'common' })}
            </a>
          </div>
        </section>
      )}
    </div>
  );
};

export default ContactActionComponent;
